import {compact} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  assertIsAccessible,
  getAccessibleOrInaccessibleId,
  isInaccessible,
} from 'graphql-client/InaccessibleInfo';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {
  FlowchartEditorMode,
  FEATURE_OVERRIDES_FOR_EDITOR_MODE,
  ProcessFlowchartEditorModeFeatureType,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {
  EdgeIdsToEdgesMap,
  makeRouterConfig,
} from 'processes/graph-builder-processes/flowchart-viz/util/makeProcessFlowchartInteractiveNode';
import {assertNever} from 'util/Types';

import {ProcessTaskDefinitionGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  ProcessTaskDefinitionVersionGraphQL,
  ProcessFlowchartConfigVersionGQL,
} from './processFlowchartConfigEditorConstants';
import {StaticFlowchartNodeData, RouterStaticNodeData} from './processFlowchartConfigEditorNodeTypes';
import {
  createStaticNodeDataFromRouterConfig,
  createEndNodeStaticData,
  createStaticNodeDataFromTaskDefinitionVersion,
  createInaccessibleStaticNodeData,
  getTaskSchemaIdFromTaskSchemaNodeDetails,
  isTaskSchemaNodeDetailsInaccessible,
} from './processFlowchartConfigEditorUtils';

export interface ConvertFlowchartConfigVersionToNodesResult {
  nodesStaticData: Array<StaticFlowchartNodeData>;
  cidToIdMap: CidIdMap;
  nodeIdPairToEdgeLabel: Record<string, string>;
}

/** Converts a GraphQL flowchart config version into static node data for the editor. */
export function convertFlowchartConfigVersionToNodes({
  flowchartConfigVersion,
  rootTaskDefinition,
  rootTaskSchema,
  allTaskSchemas,
  editorMode,
}: {
  flowchartConfigVersion: ProcessFlowchartConfigVersionGQL | null;
  rootTaskDefinition: ProcessTaskDefinitionGraphQL & {
    latestVersion: ProcessTaskDefinitionVersionGraphQL;
  };
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  editorMode: FlowchartEditorMode;
}): ConvertFlowchartConfigVersionToNodesResult {
  const cidToIdMap: CidIdMap = {};
  const nodeIdPairToEdgeLabel: Record<string, string> = {};

  let nodesStaticData: Array<StaticFlowchartNodeData>;

  if (flowchartConfigVersion?.templateFlowchart == null) {
    const rootTaskDefinitionVersion = rootTaskDefinition.latestVersion;
    nodesStaticData = [
      createStaticNodeDataFromTaskDefinitionVersion({
        nodeClientId: 'root',
        taskDefinitionVersion: rootTaskDefinitionVersion,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
        allTaskSchemas,
        editorMode,
      }),
    ];
    if (rootTaskSchema.outputSchema != null) {
      const outputNode = createStaticNodeDataFromTaskDefinitionVersion({
        nodeClientId: 'output',
        taskDefinitionVersion: rootTaskDefinitionVersion,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
        allTaskSchemas,
        editorMode,
      });
      nodesStaticData.push(outputNode);
    }
  } else {
    const shouldIncrementNodeNames =
      FEATURE_OVERRIDES_FOR_EDITOR_MODE[editorMode][
        ProcessFlowchartEditorModeFeatureType.SHOW_NODE_NAME_INCREMENT
      ];
    const existingNodeNames: Array<string> | undefined = shouldIncrementNodeNames ? [] : undefined;

    nodesStaticData = compact(
      flowchartConfigVersion.templateFlowchart.nodeConfigs.map((node) => {
        let clientNode: StaticFlowchartNodeData | null = null;
        switch (node.nodeDetails.__typename) {
          case 'ProcessRootNodeDetailsNode':
          case 'ProcessOutputNodeDetailsNode':
          case 'ProcessTaskNodeDetailsNode': {
            const taskDefinitionVersion = node.nodeDetails.processTaskDefinitionVersion;
            if (isTaskSchemaNodeDetailsInaccessible(node.nodeDetails)) {
              clientNode = createInaccessibleStaticNodeData({
                nodeClientId: node.id,
                nodeDetailsType: node.nodeDetails.type,
                taskSchemaId: getTaskSchemaIdFromTaskSchemaNodeDetails(node.nodeDetails),
                taskDefinitionVersionClientId: isInaccessible(taskDefinitionVersion)
                  ? getAccessibleOrInaccessibleId(taskDefinitionVersion)
                  : taskDefinitionVersion.id,
                executionType: rootTaskSchema.executionType,
              });
            } else {
              const accessibleTaskDefinitionVersion = assertIsAccessible(taskDefinitionVersion);
              clientNode = createStaticNodeDataFromTaskDefinitionVersion({
                nodeClientId: node.id,
                taskDefinitionVersion: accessibleTaskDefinitionVersion as ProcessTaskDefinitionVersionGraphQL,
                nodeDetailsType: node.nodeDetails.type,
                allTaskSchemas,
                existingNodeNames,
                editorMode,
                nodeConfigName: node.name,
              });
            }
            break;
          }
          case 'ProcessRouterNodeDetailsNode': {
            const edgeIdsToEdges: EdgeIdsToEdgesMap = {};
            for (const edgeConfig of flowchartConfigVersion.templateFlowchart?.edgeConfigs ?? []) {
              if (edgeIdsToEdges[edgeConfig.id] == null) {
                edgeIdsToEdges[edgeConfig.id] = [];
              }
              edgeIdsToEdges[edgeConfig.id].push({
                clientId: edgeConfig.id,
                fromNodeClientId: edgeConfig.fromNodeConfig.id,
                toNodeClientId: edgeConfig.toNodeConfig.id,
              });
            }

            const routerConfig = makeRouterConfig(
              node.nodeDetails.name,
              node.nodeDetails.routerFunctions,
              edgeIdsToEdges
            );
            const routerNode = createStaticNodeDataFromRouterConfig(node.id, routerConfig);
            extractEdgeLabels(routerNode, nodeIdPairToEdgeLabel);
            clientNode = routerNode;
            break;
          }
          case 'ProcessEndNodeDetailsNode':
            clientNode = createEndNodeStaticData(node.id, node.name);
            break;
          default:
            assert(false, `Unexpected node type ${assertNever(node.nodeDetails)}`);
            return null;
        }
        if (clientNode == null) {
          return null;
        }
        cidToIdMap[node.id] = clientNode.id;
        existingNodeNames?.push(clientNode.nodeName);
        return clientNode;
      })
    );
  }

  return {nodesStaticData, cidToIdMap, nodeIdPairToEdgeLabel};
}

/** Key for looking up edge labels between two nodes. */
export const getNodeIdPairKeyForEdgeLabel = (fromNodeId: string, toNodeId: string): string =>
  `${fromNodeId}-${toNodeId}`;

/** Populates edge label map from a router node's function specs. */
function extractEdgeLabels(node: RouterStaticNodeData, nodeIdPairToEdgeLabel: Record<string, string>): void {
  for (const functionSpec of node.routerConfig.functionSpecs) {
    const fromNodeId = functionSpec.edgeConfigReference?.fromNodeClientId;
    const toNodeId = functionSpec.edgeConfigReference?.toNodeClientId;
    if (fromNodeId == null || toNodeId == null) {
      continue;
    }
    nodeIdPairToEdgeLabel[getNodeIdPairKeyForEdgeLabel(fromNodeId, toNodeId)] = functionSpec.name;
  }
  if (node.routerConfig.defaultFunctionSpec != null) {
    const fromNodeId = node.routerConfig.defaultFunctionSpec.edgeConfigReference?.fromNodeClientId;
    const toNodeId = node.routerConfig.defaultFunctionSpec.edgeConfigReference?.toNodeClientId;
    if (fromNodeId != null && toNodeId != null) {
      nodeIdPairToEdgeLabel[getNodeIdPairKeyForEdgeLabel(fromNodeId, toNodeId)] =
        node.routerConfig.defaultFunctionSpec.name;
    }
  }
}
